import React, { useEffect, useRef } from 'react'
import { Spinner, YStack } from '@red-ui/components'
import { logger } from '@vrw/data/src/utils'
import { useAppDispatch } from '@/redux/hooks/hooks'
import { loginFailure, loginSuccess } from '@vrw/data/src/redux/auth/actions'
import { sendMessageToNative } from '@red-ui/bridge'
import { postAuthCallback, setHybridAppState } from '../../helpers/authHelper'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { persistor } from '@/redux/configureStore'
import { doFetchProfile } from '@vrw/data/src/redux/profile/dispatchers'

interface PKCEValues {
  code_verifier: string
  code_challenge: string
}

export const AuthCallback = () => {
  const dispatch = useAppDispatch()
  const hasRunRef = useRef(false)

  useEffect(() => {
    const handleCallback = async () => {
      if (hasRunRef.current) return
      hasRunRef.current = true

      try {
        const urlParams = new URLSearchParams(window.location.search)
        const state = urlParams.get('state')
        const codeVerifier = urlParams.get('code_verifier')
        const codeChallenge = urlParams.get('code_challenge')

        if (!state) {
          throw new Error('No state parameter found in URL')
        }

        const pkce: PKCEValues | undefined =
          codeVerifier && codeChallenge ? { code_verifier: codeVerifier, code_challenge: codeChallenge } : undefined

        await setHybridAppState(state, pkce)
        const callbackResult = await postAuthCallback()
        if (!callbackResult?.access_token) {
          throw new Error('No access token found in auth response')
        }

        dispatch(loginSuccess())
        await persistor.flush()
        await dispatch(doFetchProfile())

        sendMessageToNative({ type: 'auth_success' })
      } catch (error) {
        logger.error('AuthCallback: Failed to handle callback', error)
        dispatch(loginFailure('AuthCallback: Failed to handle callback'))
        sendMessageToNative({ type: 'auth_failure' })
      }
    }
    handleCallback()
  })

  return (
    <HelmetProvider>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <YStack fullscreen justifyContent="center" alignItems="center">
        <Spinner size="large" color="$red6" />
      </YStack>
    </HelmetProvider>
  )
}
